import { WebEvents } from '@serverlessinc/core-events'
import { session } from 'auth/session'
import { useCurrentOrg } from 'common/hooks/useCurrentOrg'
import { publishEvent } from 'common/utils/events'
import { cloneDeep, keys, snakeCase } from 'lodash'
import queryString from 'query-string'
import { useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useMemoCompare } from './useMemoCompare'

const { WebPageViewedV2Event } = WebEvents

export const usePathReporter = () => {
  const {
    org: { orgUid: orgId },
  } = useCurrentOrg()
  const { pathname: url } = useLocation()

  const params = useParams()
  const finalParams = useMemoCompare(
    params,
    (prev, next) => JSON.stringify(prev) === JSON.stringify(next)
  )
  const queryParams = useMemo(
    () => queryString.parse(window.location.search),
    [window.location.search]
  )

  useEffect(() => {
    const run = async () => {
      if (session.isAccountSetup() && orgId) {
        try {
          // Convert path to relative path
          const path = keys(finalParams).reduce((str, key) => {
            const value = finalParams[key]
            return str.replace(`/${value}/`, `/:${key}/`)
          }, url)

          const userId = session.get('user')?.userUid
          /**
           * Remove some query parameters, that we don't want to log/track
           */
          const parsedQs = cloneDeep(queryParams)
          delete parsedQs.code
          delete parsedQs.transactionId
          const props = {
            orgId,
            path,
            url,
            userId,
            params: keys(finalParams || {}).reduce(
              (obj, key) => ({
                ...obj,
                [snakeCase(key)]: finalParams[key],
              }),
              {}
            ),
            queryParameters: keys(parsedQs || {}).reduce(
              (obj, key) => ({
                ...obj,
                [snakeCase(key)]: parsedQs[key],
              }),
              {}
            ),
          }

          /**
           * Send event
           */
          const pageViewEvent = new WebPageViewedV2Event(props)
          await publishEvent(pageViewEvent)
        } catch (e) {
          console.error('Failed to send page event', e)
        }
      }
    }
    run()
  }, [url, finalParams, queryParams])

  return null
}
